import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { api } from "../api/client";
import { useSSEQuery } from "../hooks/useSSEQuery";
import { usePollingQuery } from "../hooks/usePollingQuery";
import { useIncidentAcks } from "../hooks/useIncidentAcks";
import { useSetting } from "../settings/SettingsContext";
import { useTheme } from "../theme/ThemeContext";
import type { ServiceAlert, VehiclePosition, TripInstance, Vehicle, DeviceViewResponse, DriverMessage, FleetStats } from "../api/types";
import { hasFreshFix } from "../api/types";

import TopBar from "./TopBar";
import LeftRail, { RailItem } from "./LeftRail";
import StatusBar from "./StatusBar";
import FleetMap from "./FleetMap";
import MapSidebar from "./MapSidebar";
import MapStatsOverlay from "./MapStatsOverlay";
import MapLegend from "./MapLegend";
import {
  buildLiveRows,
  computeFleetStats,
  listOrder,
  routeOptions,
  rowMatchesQuery,
  rowOnRoute,
  type LiveVehicleRow,
  type RouteOption,
} from "./liveFleet";

const FLEET_FALLBACK_POLL_MS = 3000;
const ALERTS_FALLBACK_POLL_MS = 5000;
const NO_POSITIONS: VehiclePosition[] = [];

/** What the shell hands the pages it overlays (useOutletContext). */
export interface LayoutOutletContext {
  positions: VehiclePosition[];
  activeTripsByVehicle: Map<string, TripInstance>;
  activeTrips: TripInstance[];
  /** The live rows by marker key — the one resolved state per bus. */
  liveRows: ReadonlyMap<string, LiveVehicleRow>;
  /** The rows' states are facts: positions, devices and trips have arrived. */
  liveKnown: boolean;
  /** fleetStats is a count of the fleet: liveKnown, and the roster loaded. */
  statsKnown: boolean;
  fleetStats: FleetStats;
  /** Vehicles with an active panic alarm, drawn as such on any map. */
  panicVehicleIds?: ReadonlySet<string>;
  alerts: ServiceAlert[];
  theme?: "dark" | "light";
}

// Vite resolves import.meta.env at build time; keep the same prefix the
// API client uses so the SSE endpoint hits the same host.
const API_BASE = import.meta.env.VITE_API_BASE_URL || "/api/v1";

const RAIL: RailItem[] = [
  // OPERATIONS
  { to: "/dashboard", label: "Dashboard",  icon: "dashboard", section: "Operations" },
  { to: "/map",       label: "Live map",   icon: "map",       section: "Operations" },
  { to: "/trips",     label: "Trips",      icon: "trips",     section: "Operations" },
  { to: "/mileage",   label: "Run kilometres", icon: "odometer", section: "Operations" },
  { to: "/stop-templates", label: "Routes", icon: "routes",   section: "Operations", roles: ["admin", "operations_supervisor"] },
  { to: "/playback",  label: "History",    icon: "history",   section: "Operations" },
  { to: "/schedules", label: "Schedules",  icon: "schedules", section: "Operations" },
  // COMMUNICATIONS
  { to: "/messages",  label: "Messages",   icon: "messages",  section: "Communications" },
  { to: "/alerts",    label: "Alerts",     icon: "alerts",    section: "Communications" },
  { to: "/panic",     label: "Panic queue", icon: "panic",    section: "Communications" },
  // ADMINISTRATION
  { to: "/reassign",  label: "Reassignment", icon: "reassign", section: "Administration" },
  { to: "/reports",   label: "Reports",    icon: "reports",   section: "Administration" },
  { to: "/devices",       label: "Devices",      icon: "devices",   section: "Administration", roles: ["admin", "operations_supervisor"] },
  { to: "/pid-devices",  label: "PID Devices",  icon: "devices",   section: "Administration", roles: ["admin", "operations_supervisor"] },
  { to: "/pid-tickers",  label: "PID Tickers",  icon: "alerts",    section: "Administration", roles: ["admin", "operations_supervisor"] },
  { to: "/firmware",     label: "Firmware",     icon: "firmware",  section: "Administration", roles: ["admin", "operations_supervisor"] },
  { to: "/settings",     label: "Settings",     icon: "settings",  section: "Administration", roles: ["admin", "operations_supervisor"] },
  { to: "/users",        label: "Users",        icon: "users",     section: "Administration", roles: ["admin"] },
];

function operatorLabel(displayName: string | undefined, employeeId: string): { name: string; initials: string } {
  const name = displayName?.trim() || employeeId || "Operator";
  const parts = name.split(/\s+/).filter(Boolean);
  const initials = (
    parts.length >= 2
      ? parts[0][0] + parts[parts.length - 1][0]
      : name.replace(/[^a-zA-Z]/g, "").slice(0, 2)
  ).toUpperCase() || "OP";
  return { name, initials };
}

const EXPAND_PATH = "M4 9V4h5 M20 9V4h-5 M4 15v5h5 M20 15v5h-5";
const COLLAPSE_PATH = "M9 4v5H4 M15 4v5h5 M9 20v-5H4 M15 20v-5h5";

function FullscreenToggle({ isFullscreen, onToggle }: { isFullscreen: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      className="map-fullscreen-btn"
      onClick={onToggle}
      title={isFullscreen ? "Exit full screen" : "Full screen"}
      aria-label={isFullscreen ? "Exit full screen" : "Full screen"}
    >
      <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <path d={isFullscreen ? COLLAPSE_PATH : EXPAND_PATH} />
      </svg>
    </button>
  );
}

export default function Layout() {
  const { session, signOut, refreshAccessToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const mapSectionRef = useRef<HTMLDivElement>(null);

  // Fullscreen state lives here (not inside FullscreenToggle) so the sidebar
  // dock can hide while the map is fullscreened — the toggle's target and the
  // dock it affects are siblings, not parent/child.
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  useEffect(() => {
    const onChange = () => setIsMapFullscreen(document.fullscreenElement === mapSectionRef.current);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);
  const toggleMapFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      mapSectionRef.current?.requestFullscreen().catch(() => {
        // Fullscreen can be denied (e.g. permissions-policy in an iframe);
        // the button just stays in its normal state if so.
      });
    }
  }, []);

  const operator = useMemo(() => {
    const { name, initials } = operatorLabel(
      session?.profile?.displayName,
      session?.employeeId ?? "Operator"
    );
    return { name, initials, role: session?.role ?? "dispatcher" };
  }, [session?.profile?.displayName, session?.employeeId, session?.role]);

  // Persistent fleet-status stream (SSE) with polling fallback. On a drop,
  // proactively refresh the access token — EventSource can't tell us a
  // failure was a 401, and waiting on the fallback poll's own 401 handling
  // to notice left the stream reconnecting on a dead token until that poll
  // happened to fire, which is what "live data freezes" looked like.
  const tokenGetter = () => session?.accessToken ?? null;
  const onStreamError = useCallback(() => { void refreshAccessToken(); }, [refreshAccessToken]);
  const fleetQuery = useSSEQuery<VehiclePosition[]>(
    `${API_BASE}/realtime/fleet-status/stream`,
    tokenGetter,
    () => api.fleetStatus().then((d) => (Array.isArray(d) ? d : [])),
    [session?.accessToken],
    { fallbackIntervalMs: FLEET_FALLBACK_POLL_MS, onConnectionError: onStreamError }
  );
  const positions = fleetQuery.data ?? NO_POSITIONS;

  // Active-alerts stream (SSE) — drives both the dock and the panic banner.
  const alertsQuery = useSSEQuery<ServiceAlert[]>(
    `${API_BASE}/alerts/stream`,
    tokenGetter,
    () => api.listActiveAlerts().then((d) => (Array.isArray(d) ? d : [])),
    [session?.accessToken],
    { fallbackIntervalMs: ALERTS_FALLBACK_POLL_MS, onConnectionError: onStreamError }
  );
  const alerts = alertsQuery.data ?? [];

  // Active (in-progress) trips keyed by vehicle, so the live map can label each
  // bus with its driver / route / progress. Polled and joined on the client —
  // the fleet-status SSE only carries raw positions.
  const tripsQuery = usePollingQuery<TripInstance[]>(
    () => api.listTripInstances({ status: "in_progress" }).then((d) => (Array.isArray(d) ? d : [])),
    [session?.accessToken],
    { intervalMs: 5000 }
  );
  const activeTripsByVehicle = useMemo(() => {
    const m = new Map<string, TripInstance>();
    for (const t of tripsQuery.data ?? []) {
      if (!t.vehicle_id) continue;
      // A bus can briefly have two open trips, until the server's trip
      // closer supersedes the older one; show the one it started last.
      const prev = m.get(t.vehicle_id);
      const started = (x?: TripInstance) => (x?.actual_start ? new Date(x.actual_start).getTime() : 0);
      if (!prev || started(t) > started(prev)) m.set(t.vehicle_id, t);
    }
    return m;
  }, [tripsQuery.data]);

  // Messages awaiting acknowledgment, for the sidebar badge. 403s for
  // roles without dispatcher access (e.g. a driver session) are swallowed —
  // the badge simply doesn't show rather than erroring the whole shell.
  const messagesQuery = usePollingQuery<DriverMessage[]>(
    () => api.listAgencyMessages({ needsAckOnly: true, limit: 200 }).catch(() => []),
    [session?.accessToken],
    { intervalMs: 15000 }
  );
  const needsAckCount = messagesQuery.data?.length ?? 0;

  // Fleet roster — gives every bus its plate so the map can label even
  // vehicles that have never logged in or are currently offline (last-known
  // position still renders). Refreshed lazily; the roster rarely changes.
  const vehiclesQuery = usePollingQuery<Vehicle[]>(
    () => api.listVehicles().then((d) => (Array.isArray(d) ? d : d?.data ?? [])),
    [session?.accessToken],
    { intervalMs: 60000 }
  );

  // Device sessions and heartbeats: whether each bus's tablet is reachable,
  // and who — if anyone — is signed in on it.
  const devicesQuery = usePollingQuery<DeviceViewResponse>(
    () => api.listDevices(),
    [session?.accessToken],
    { intervalMs: 10000 }
  );

  // Ticks every 15s purely so states re-derive as heartbeats/positions age
  // past their thresholds even when no new data has arrived — otherwise a
  // vehicle that stopped reporting would visually freeze in its last state
  // instead of degrading to offline/no_gps.
  const [stateTick, setStateTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setStateTick((n) => n + 1), 15000);
    return () => window.clearInterval(id);
  }, []);

  // One row per bus, with one merged lifecycle state — device heartbeat, GPS
  // fix, and trip/session are independent signals, so this is the one place
  // that reconciles them (liveFleet.ts) instead of every consumer re-deriving
  // its own partial answer. The map, the list, the detail panel and the
  // counters all read these rows. Covers the full roster, not just vehicles
  // currently reporting a position, so an offline bus is still representable
  // rather than silently absent.
  const liveRows = useMemo(
    () => buildLiveRows({
      vehicles: vehiclesQuery.data ?? [],
      positions,
      devices: devicesQuery.data?.devices ?? [],
      activeTrips: activeTripsByVehicle,
      now: Date.now(),
      rosterKnown: vehiclesQuery.data != null,
    }),
    // stateTick: re-derive as fixes and heartbeats age (see above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vehiclesQuery.data, positions, devicesQuery.data, activeTripsByVehicle, stateTick]
  );
  const liveRowsByKey = useMemo(() => new Map(liveRows.map((r) => [r.key, r])), [liveRows]);

  // Whether those states are facts yet. Until the first fleet snapshot, the
  // roster, device sessions and trips have all come back (or failed), a state
  // resolved from what has arrived is an artefact of what has not: on a fresh
  // load, while an expired access token was refreshed, every bus with a fresh
  // heartbeat resolved to "No GPS" for want of positions, and the counters
  // and the rail reported it as the state of the fleet.
  //
  // Each of these means "has ever arrived", not "the last poll succeeded":
  // tying it to the error meant one failed refresh blanked the counters to
  // "—" while the sidebar carried on listing all 35 buses from the data it
  // already had.
  //
  // Only the positions must succeed. A roster that will not load — a 429, a
  // 5xx — leaves the buses on the road unlabelled, but on the map: it used
  // to take every one of them off it. It gates the counters instead, which
  // count the fleet and are meaningless without it.
  const rosterLoaded = vehiclesQuery.data != null;
  const positionsLoaded = fleetQuery.data !== undefined;
  const liveKnown =
    positionsLoaded &&
    (rosterLoaded || vehiclesQuery.error != null) &&
    (devicesQuery.data !== undefined || devicesQuery.error != null) &&
    (tripsQuery.data !== undefined || tripsQuery.error != null);
  const statsKnown = liveKnown && rosterLoaded;

  const { theme } = useTheme();

  // Highest-priority unacked PANIC, if any.
  const panicAlert = alerts.find(
    (a) => a.alert_type === "PANIC" && a.is_active && a.severity === "SEVERE"
  );
  const panicVehicleId = panicAlert?.metadata?.vehicle_id;
  const panicCount = alerts.filter((a) => a.alert_type === "PANIC" && a.is_active).length;
  // Every bus with an active panic is drawn as one, not only the one the
  // banner and the alarm are about.
  const panicKey = alerts
    .filter((a) => a.alert_type === "PANIC" && a.is_active && a.metadata?.vehicle_id)
    .map((a) => a.metadata.vehicle_id!)
    .sort()
    .join(",");
  const panicVehicleIds = useMemo(
    () => (panicKey ? new Set(panicKey.split(",")) : undefined),
    [panicKey]
  );

  const [selectedVehicleId, setSelectedVehicleId] = useState<string | undefined>(undefined);
  // Bumped on every explicit selection so the map flies to the bus even when
  // it was already selected (e.g. after panning away).
  const [focusRequest, setFocusRequest] = useState(0);
  const [routeFilter, setRouteFilter] = useState<RouteOption | null>(null);
  // The vehicle list's filter, lifted here so the top bar's search box is the
  // same filter rather than a box wired to nothing.
  const [vehicleQuery, setVehicleQuery] = useState("");

  // Route filter. Matches on the route of the trip a bus is running, the same
  // key the pills are built from — and a bus raising a panic is shown whatever
  // the filter.
  const routeKey = routeFilter?.key ?? null;
  const onRouteRows = useMemo(
    () => liveRows.filter((r) => rowOnRoute(r, routeKey, panicVehicleIds)),
    [liveRows, routeKey, panicVehicleIds]
  );
  const mapVisibleKeys = useMemo(
    () => (routeKey ? new Set(onRouteRows.map((r) => r.key)) : undefined),
    [routeKey, onRouteRows]
  );
  const listRows = useMemo(
    () => onRouteRows.filter((r) => rowMatchesQuery(r, vehicleQuery)).sort(listOrder(panicVehicleIds)),
    [onRouteRows, vehicleQuery, panicVehicleIds]
  );
  // The top bar's search looks across the whole fleet, not only the route the
  // list is narrowed to: a bus on another line is still one an operator may
  // be looking for, and picking it drops the route filter (selectVehicle).
  const searchRows = useMemo(
    () => (vehicleQuery.trim()
      ? liveRows.filter((r) => rowMatchesQuery(r, vehicleQuery)).sort(listOrder(panicVehicleIds))
      : []),
    [liveRows, vehicleQuery, panicVehicleIds]
  );
  const routeOpts = useMemo(() => {
    const opts = routeOptions(liveRows);
    // Keep the chosen route's pill after its last trip ends, so the filter
    // that is now hiding every bus can still be seen and cleared.
    if (routeFilter && !opts.some((o) => o.key === routeFilter.key)) opts.push({ ...routeFilter, count: 0 });
    return opts;
  }, [liveRows, routeFilter]);

  // A bus picked by name — the list, the search, Locate — must be on screen:
  // drop a route filter that would hide it.
  const selectVehicle = useCallback((key: string | undefined) => {
    setSelectedVehicleId(key);
    if (!key) return;
    setFocusRequest((n) => n + 1);
    const row = liveRowsByKey.get(key);
    if (routeKey && (!row || !rowOnRoute(row, routeKey, panicVehicleIds))) setRouteFilter(null);
  }, [liveRowsByKey, routeKey, panicVehicleIds]);

  // And choosing a route the selected bus is not on deselects it, so the
  // detail panel never describes a bus the map no longer shows.
  const chooseRoute = useCallback((route: RouteOption | null) => {
    setRouteFilter(route);
    if (!route || !selectedVehicleId) return;
    const row = liveRowsByKey.get(selectedVehicleId);
    if (!row || !rowOnRoute(row, route.key, panicVehicleIds)) setSelectedVehicleId(undefined);
  }, [selectedVehicleId, liveRowsByKey, panicVehicleIds]);

  const prevPanicVehicleRef = useRef<string | undefined>(panicVehicleId);
  useEffect(() => {
    if (panicVehicleId && panicVehicleId !== prevPanicVehicleRef.current) {
      setSelectedVehicleId(panicVehicleId);
    }
    prevPanicVehicleRef.current = panicVehicleId;
  }, [panicVehicleId]);

  // Ack state lives here (not inside MapSidebar) because the panic alarm
  // below needs to see the same acknowledgement the sidebar's "Acknowledge"
  // button records, in this same tab, the moment it's clicked.
  const { acks: incidentAcks, ack: ackIncident } = useIncidentAcks(operator.name);
  const panicAcked = panicAlert ? !!incidentAcks[panicAlert.id] : false;

  // Audible alarm for an active, unacknowledged panic (real Settings toggle,
  // occ.panic_audible_alarm). Synthesized via Web Audio — no asset file.
  // Repeats every 1.5s for as long as the alert stays active and unacked —
  // a single 0.35s beep is easy to miss in a busy control room — and stops
  // the instant it's acknowledged or resolved.
  const audibleAlarmEnabled = useSetting<boolean>("occ.panic_audible_alarm", true);
  useEffect(() => {
    if (!panicAlert || panicAcked || !audibleAlarmEnabled) return;
    let cancelled = false;
    const beep = () => {
      try {
        const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "square";
        osc.frequency.value = 880;
        gain.gain.setValueAtTime(0.15, ctx.currentTime);
        osc.connect(gain).connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + 0.35);
        osc.onended = () => void ctx.close();
      } catch {
        // Autoplay/permission restrictions — the visual cues (red frame,
        // sidebar badge, panic queue) still carry the alert regardless.
      }
    };
    beep();
    const id = window.setInterval(() => { if (!cancelled) beep(); }, 1500);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [panicAlert?.id, panicAcked, audibleAlarmEnabled]);

  // The one on-duty/status computation for the whole screen — TopBar and
  // MapStatsOverlay both render this same object instead of each deriving
  // its own (they used to: TopBar from vehicleStatusFromPosition() over raw
  // positions, MapStatsOverlay from vehicleLiveStates() over the roster —
  // two different taxonomies over two different denominators, so they could
  // and did disagree). It counts the same rows the map and the list draw.
  // On duty = every bus that is up and reporting, not only ones on a formal
  // trip: on an open-pool fleet a bus with a live fix and no trip is a
  // working bus, so ON DUTY is the roster minus offline and no-GPS.
  const fleetStats: FleetStats = useMemo(() => computeFleetStats(liveRows), [liveRows]);
  const liveCount = useMemo(() => {
    const now = Date.now();
    return liveRows.filter((r) => hasFreshFix(r.position, now)).length;
  }, [liveRows]);

  // Buses that are reporting but have nobody signed in — a real
  // backend-computed flag (heartbeating with no session for hours), distinct
  // from vehicleLiveStates' "unclaimed" (which just means "no driver right
  // now", not "abandoned"). Drives the fleet-wide banner below: if every
  // vehicle currently reporting is signed out, that's not a handful of idle
  // buses, it's nobody logged in anywhere.
  // Real data age: the newest GPS fix anywhere in the fleet. The status card
  // and footer used to report the SSE transport instead — the server ticks a
  // frame every 2s whether or not it has anything new, so a pipeline that had
  // been dead for weeks still rendered "All systems live · GPS streaming" and
  // "Last update 0s ago". Health now follows the data, not the socket.
  const freshestFixAt = useMemo(() => {
    let newest = 0;
    for (const p of positions) {
      const t = new Date(p.time).getTime();
      if (Number.isFinite(t) && t > newest) newest = t;
    }
    return newest || null;
  }, [positions]);

  // Requests the shell cannot do without. A failure here used to be swallowed
  // (`data ?? []`), so a 401/403/429 rendered as a fleet of zero vehicles —
  // indistinguishable from an agency that owns no buses.
  const dataError = useMemo(() => {
    if (vehiclesQuery.error) {
      // A failed refresh is not the same as having nothing. When a previous
      // poll succeeded the roster on screen is real, just ageing, and saying
      // "unavailable" over a full vehicle list reads as a contradiction.
      const stale = vehiclesQuery.data != null;
      if (vehiclesQuery.errorStatus === 429) {
        return stale
          ? "Rate limited by the API gateway — the fleet roster below may be out of date."
          : "Rate limited by the API gateway — the fleet roster could not be loaded.";
      }
      return stale
        ? `Fleet roster may be out of date — last refresh failed: ${vehiclesQuery.error}`
        : `Fleet roster unavailable: ${vehiclesQuery.error}`;
    }
    if (devicesQuery.error) {
      return devicesQuery.errorStatus === 403
        ? "This account cannot read device sessions, so vehicles cannot be classified online/offline."
        : `Device sessions unavailable: ${devicesQuery.error}`;
    }
    // Without the trip list a bus on a trip reads as one with no trip — say
    // so rather than let the map state it.
    if (tripsQuery.error) {
      return tripsQuery.data != null
        ? `Trips may be out of date — last refresh failed: ${tripsQuery.error}`
        : `Trips unavailable: ${tripsQuery.error} — buses on a trip may show as having none.`;
    }
    return null;
  }, [vehiclesQuery.error, vehiclesQuery.errorStatus, devicesQuery.error, devicesQuery.errorStatus, tripsQuery.error, tripsQuery.data]);

  const activeCount = alerts.filter((a) => a.is_active).length;
  const severeAlert = alerts.some((a) => a.is_active && a.severity === "SEVERE");

  const currentRole = (session?.role ?? "").toLowerCase().trim().replace(/[\s-]+/g, "_");
  const railItems: RailItem[] = RAIL.map((r) => {
    if (r.to === "/panic" && panicCount > 0) return { ...r, badge: panicCount, badgeTone: "crit" as const };
    if (r.to === "/messages" && needsAckCount > 0) return { ...r, badge: needsAckCount, badgeTone: "ok" as const };
    if (r.to === "/alerts" && activeCount > 0) return { ...r, badge: activeCount, badgeTone: "ok" as const };
    return r;
  });

  const onResolve = useCallback(async (alertId: string) => {
    // SSE picks up the deactivation on the next ticker frame; no manual
    // refetch needed here.
    await api.deactivateAlert(alertId);
  }, []);

  const handleSignOut = useCallback(() => { signOut(); navigate("/login"); }, [signOut, navigate]);

  // The persistent live FleetMap is the default backdrop. "/" and "/map" show
  // it uncovered (the Fleet Map nav target); every other surface — including
  // /playback (historical scrub) — renders as an opaque overlay on top.
  const isMapRoute = location.pathname === "/" || location.pathname === "/map";
  const showDock = isMapRoute;

  // Enter in the top bar's search: select the first match, and bring up the
  // map if another page is covering it.
  const submitSearch = useCallback(() => {
    const first = liveKnown ? searchRows[0] : undefined;
    if (!first) return;
    selectVehicle(first.key);
    if (!isMapRoute) navigate("/map");
  }, [liveKnown, searchRows, selectVehicle, isMapRoute, navigate]);
  const openAlerts = useCallback(() => navigate("/alerts"), [navigate]);

  const outletContext: LayoutOutletContext = {
    positions,
    activeTripsByVehicle,
    activeTrips: tripsQuery.data ?? [],
    liveRows: liveRowsByKey,
    liveKnown,
    statsKnown,
    fleetStats,
    panicVehicleIds,
    alerts,
    theme,
  };

  return (
    <div className="occ-frame">
      <LeftRail
        items={railItems}
        currentRole={currentRole}
        onSignOut={handleSignOut}
        connected={!fleetQuery.error && !alertsQuery.error}
        connecting={!positionsLoaded}
        freshestFixAt={freshestFixAt}
        dataError={dataError}
      />

      <div className="occ-main">
        <TopBar
          fleetStats={fleetStats}
          operator={operator}
          statsKnown={statsKnown}
          search={{
            value: vehicleQuery,
            onChange: setVehicleQuery,
            onSubmit: submitSearch,
            matches: vehicleQuery.trim() && liveKnown ? searchRows.length : null,
          }}
          alertCount={activeCount}
          alertSevere={severeAlert}
          onOpenAlerts={openAlerts}
        />

        {dataError && (
          <div className="occ-signedout-banner" role="alert">
            <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L14.71 3.86a2 2 0 0 0-3.42 0Z" />
            </svg>
            <span>{dataError}</span>
          </div>
        )}

        <div className={"occ-mid" + (showDock && !isMapFullscreen ? "" : " no-dock")} ref={mapSectionRef}>
          <div className="occ-center">
            <FleetMap
              rows={liveRowsByKey}
              liveKnown={liveKnown}
              visibleKeys={mapVisibleKeys}
              selectedVehicleId={selectedVehicleId}
              onSelectVehicle={selectVehicle}
              focusRequest={focusRequest}
              panicVehicleIds={panicVehicleIds}
              theme={theme}
              // The backdrop map sits under every other page, the dashboard
              // that "/" lands on included. Framing the corridor there used
              // up the one-time fit at the wrong size and padding, before
              // the operator ever saw the map.
              fitOnLoad={isMapRoute}
              hud={isMapRoute ? (
                <MapStatsOverlay
                  stats={fleetStats}
                  statsKnown={statsKnown}
                  routes={routeOpts}
                  routeFilter={routeFilter}
                  onRouteFilter={chooseRoute}
                />
              ) : undefined}
              controls={isMapRoute ? <FullscreenToggle isFullscreen={isMapFullscreen} onToggle={toggleMapFullscreen} /> : undefined}
            />
            {isMapRoute && <MapLegend />}
            {!isMapRoute ? (
              <div className="occ-page-overlay">
                <Outlet context={outletContext} />
              </div>
            ) : null}
          </div>

          {showDock && !isMapFullscreen && (
            <MapSidebar
              rows={listRows}
              selectedRow={selectedVehicleId ? liveRowsByKey.get(selectedVehicleId) : undefined}
              liveKnown={liveKnown}
              rosterLoaded={rosterLoaded}
              liveCount={liveCount}
              routeFilter={routeFilter}
              routeCount={onRouteRows.length}
              totalCount={liveRows.length}
              onClearRouteFilter={() => chooseRoute(null)}
              query={vehicleQuery}
              onQueryChange={setVehicleQuery}
              alerts={alerts}
              acks={incidentAcks}
              onAck={ackIncident}
              selectedVehicleId={selectedVehicleId}
              onSelectVehicle={selectVehicle}
              panicVehicleIds={panicVehicleIds}
              onResolve={onResolve}
            />
          )}
        </div>

        <StatusBar
          lastUpdatedAt={freshestFixAt}
          connected={!fleetQuery.error && !alertsQuery.error}
          connecting={!positionsLoaded}
        />
      </div>
    </div>
  );
}
